<template>
  <div>
    <div
      v-if="multiSitePayload.length > 0 && auditSamplingMode === 'siteBased'"
      :key="auditSamplingMode"
    >
      <template v-for="(siteValue, siteIndex) in siteArray">
        <div class="columns">
          <div class="column" :class="$style.headerTitleColumn">
            <label
              :class="[
                $style.tableName,
                $style.criteriaBasedLabelSiteBased,
                siteIndex === 0 ? $style.changePaddingTop : null,
              ]"
              class="label"
              >{{ siteValue.name }}</label
            >
          </div>
        </div>
        <b-table :class="$style.siteBasedTable" :data="auditCriteriaList">
          <template slot-scope="props">
            <b-table-column field="index" label="No." width="10px">
              {{ props.index + 1 }}.
            </b-table-column>
            <b-table-column
              field="criteria"
              label="Data Collection Period Criteria"
            >
              {{ props.row.criteriaTitle }}
            </b-table-column>
            <b-table-column
              width="90px"
              field="sampleSize"
              label="Sample Size"
              :header-class="$style.calculatorHeader"
            >
              <bField horizontal class="sampleSizeInputContainer">
                <b-input
                  :disabled="!editable"
                  v-model="props.row.sites[siteIndex].sampleSize"
                  type="number"
                  :class="$style.numberInput"
                  @change.native="updateTotalSamplingSize(props.index)"
                  min="0"
                  maxlength="7"
                  @keydown.native="handleNumericKeydown"
                  @input.native="enforceMaxLength"
                ></b-input>
              </bField>
            </b-table-column>
            <b-table-column width="45px">
              <template slot="header">
                <template v-if="editable">
                  <OneOffSamplingCalculator
                    :samplingMode="auditSamplingMode"
                    :criteriaArray="auditCriteriaList"
                    :initialData="siteValue.calculateData"
                    :siteName="siteValue.name"
                    @applySampleSize="handleApplySampleSize"
                  ></OneOffSamplingCalculator>
                </template>
              </template>
              <template v-if="editable">
                <OneOffSamplingCalculator
                  :samplingMode="auditSamplingMode"
                  :initialData="props.row.sites[siteIndex].calculateData"
                  :criteriaTitle="props.row.criteriaTitle"
                  :auditSiteId="siteValue.auditSiteId"
                  :auditCriteriaId="props.row.auditCriteriaId"
                  @applySampleSize="handleApplySampleSize"
                ></OneOffSamplingCalculator>
              </template>
            </b-table-column>
          </template>
        </b-table>
      </template>
    </div>
    <div v-else-if="multiSitePayload.length > 0" :key="auditSamplingMode">
      <div class="columns">
        <div class="column" :class="$style.headerTitleColumn">
          <label
            :class="[$style.tableName, $style.criteriaBasedLabel]"
            class="label"
            >CRITERIA BASED SAMPLING</label
          >
        </div>
      </div>
      <b-table :class="$style.criteriaBasedTable" :data="auditCriteriaList">
        <template slot-scope="props">
          <b-table-column field="index" label="No." width="10px">
            {{ props.index + 1 }}.
          </b-table-column>
          <b-table-column
            field="criteria"
            label="Data Collection Period Criteria"
            width="60%"
          >
            {{ props.row.criteriaTitle }}
          </b-table-column>
          <b-table-column
            width="100%"
            field="sampleSize"
            label="Sample Size"
            :header-class="$style.calculatorHeader"
          >
            <bField horizontal class="sampleSizeInputContainer">
              <b-input
                :disabled="!editable"
                v-model="props.row.sampleSize"
                type="number"
                :class="$style.numberInput"
                @change.native="updateChildSamplingSize(props.index)"
                min="0"
                maxlength="7"
                @keydown.native="handleNumericKeydown"
                @input.native="enforceMaxLength"
              ></b-input>
            </bField>
            <template v-for="(site, siteIndex) in siteArray">
              <bField
                horizontal
                class="sampleSizeInputContainer"
                :label="site.name"
              >
                <b-input
                  :disabled="!editable"
                  v-model="props.row.sites[siteIndex].sampleSize"
                  type="number"
                  :class="$style.numberInput"
                  @change.native="
                    updateOtherChildSamplingSize(props.index, siteIndex)
                  "
                  min="0"
                  maxlength="7"
                  @keydown.native="handleNumericKeydown"
                  @input.native="enforceMaxLength"
                ></b-input>
              </bField>
            </template>
          </b-table-column>
          <b-table-column field="" label="" width="45px">
            <template slot="header">
              <template v-if="editable">
                <OneOffSamplingCalculator
                  :samplingMode="auditSamplingMode"
                  :initialData="criteriaBasedCalculateData"
                  :siteArray="siteArray"
                  @applySampleSize="handleApplySampleSizeForCriteriaBased"
                ></OneOffSamplingCalculator>
              </template>
            </template>
            <template v-if="editable">
              <OneOffSamplingCalculator
                :samplingMode="auditSamplingMode"
                :initialData="props.row.calculateData"
                :siteArray="siteArray"
                :criteriaTitle="props.row.criteriaTitle"
                @applySampleSize="handleApplySampleSizeForSingleCriteriaBased"
              ></OneOffSamplingCalculator>
            </template>
          </b-table-column>
        </template>
      </b-table>
    </div>
    <div
      :class="$style.saveChangesContainer"
      v-if="editable && multiSitePayload.length > 0"
    >
      <b-button
        @click="handleMultiSiteChanges"
        :class="$style.saveChangesBtn"
        type="is-primary"
        >Save</b-button
      >
    </div>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { Action, State } from 'vuex-class';
import HeroBar from '@/components/base/HeroBar.vue';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import {
  map as _map,
  round as _round,
  cloneDeep as _cloneDeep,
  orderBy as _orderBy,
  findIndex as _findIndex,
  find as _find,
} from 'lodash';
import { ToastProgrammatic as Toast } from 'buefy';
import {
  AuditCriteriaMapStatus,
  AuditSamplingDataConfig,
  AuditSamplingResponse,
  AuditSamplingSite,
  AuditSite,
  MultiSitePayload,
  MultiSiteSamplingRequestPayload,
} from '@/store/modules/audits/types/audits.types';
import OneOffSamplingCalculator from '@/views/Audit/components/OneOffSamplingCalculator.vue';
import { distributeTotal, redistributeTotal } from '@/utils/sampling.util';

@Component({
  components: {
    OneOffSamplingCalculator,
    DefaultLayout,
    HeroBar,
  },
})
export default class TargetSampleSizeMultiSiteCriteriaSampling extends Vue {
  @Prop()
  public auditId!: number;
  @Prop()
  public auditSamplingMode!: string;
  @Prop()
  public auditConfig!: AuditSamplingResponse;
  @Prop()
  public auditSiteList!: AuditSite[];
  @Prop()
  public auditCategory!: string;
  @Prop({ default: true })
  public editable!: boolean;
  public auditStartDate: any = null;
  public multiSitePayload: MultiSitePayload[] = [];
  public siteArray: AuditSamplingSite[] = [];
  public auditSamplingType: string = 'targetSampleSize';
  public calculateData: any;
  public criteriaBasedCalculateData: any;
  public oneOffCalculateData = {
    confidenceLevel: 95,
    population: 100,
    margin: 5,
    groupOne: 80,
    recommended: 0,
    output: 0,
  };

  @Action('audits/updateMultiSiteSampling')
  public updateMultiSiteSampling!: (
    payload: MultiSiteSamplingRequestPayload,
  ) => void;

  @State((state) => state.audits.updatedMultiSiteSampling)
  public updatedMultiSiteSampling!: any;

  @Watch('updatedMultiSiteSampling')
  @isTruthy
  public watchUpdatedMultiSiteSampling() {
    this.showNotificationAndRedirectToProject();
  }

  public mounted() {
    this.populateMultiSiteData();
  }

  public populateMultiSiteData() {
    this.initializeSiteArray();
    const unsortedPayload = this.transformApiDataToPayload();
    this.multiSitePayload = this.sortPayloadSites(unsortedPayload);
  }

  /**
   * Initialize the master list of all sites for this audit. This function runs
   * during the mounted lifecycle hook of the component.
   *
   * The function creates an array of objects, where each object represents a
   * site. The following properties are included in each object:
   *
   * - `siteId`: The ID of the site.
   * - `name`: The name of the site.
   * - `sampleSize`: The sample size for the site
   * - `auditSiteId`: The ID of the audit site.
   * - `calculateData`: An object containing the calculation configuration for
   *   the site. calculateData contains the properties `auditSiteId`, `calculation`,
   *   `confidenceLevel`, `margin`, `population`, and `recommended`.
   *
   * The function also creates a `criteriaBasedCalculateData` object, which is a
   * deep copy of the `siteArray` with an additional `sites` property.
   */
  private initializeSiteArray(): void {
    // Phase 1: Create a master list of all sites for this audit.
    this.siteArray = this.auditSiteList.map((auditSite: AuditSite) => ({
      siteId: auditSite.site.id,
      name: auditSite.site.name,
      sampleSize: 0,
      auditSiteId: auditSite.id,
      calculateData: {
        ...this.findCalculationConfig(null, auditSite.id),
        auditSiteId: auditSite.id,
      },
    }));

    this.criteriaBasedCalculateData = {
      ...this.findCalculationConfig(null, null),
      sites: _cloneDeep(this.siteArray),
    };
  }

  /**
   * Transforms the grouped data from the API into the `multiSitePayload` structure.
   *
   * The `multiSitePayload` structure is an array of objects, where each object
   * represents a group of criteria. The following properties are included in
   * each object:
   *
   * - `auditId`: The ID of the audit.
   * - `criteriaId`: The ID of the criteria.
   * - `auditCriteriaId`: The ID of the audit criteria map.
   * - `criteriaTitle`: The title of the criteria.
   * - `order`: The order of the criteria.
   * - `calculateData`: An object containing the calculation configuration for
   *   the site. calculateData contains the properties `auditSiteId`, `calculation`,
   *   `confidenceLevel`, `margin`, `population`, and `recommended`.
   * - `sites`: An array of objects, where each object represents a site and
   *   contains the properties `siteId`, `name`, `sampleSize`, `auditSiteId`, and
   *   `calculateData`.
   * - `sampleSize`: The total sample size for the criteria.
   * - `dataConfig`: The data config for the criteria.
   *
   * The function also filters out any groups of criteria that do not have any
   * active configurations.
   */
  private transformApiDataToPayload(): MultiSitePayload[] {
    // Phase 2: Transform the grouped data from the API into the `multiSitePayload` structure.
    return this.auditConfig.dataConfigs
      .map((criterionGroup): MultiSitePayload | null => {
        const activeConfigs = Object.values(criterionGroup).filter(
          (config) =>
            config.auditCriteriaMap.status === AuditCriteriaMapStatus.ACTIVE,
        );

        if (activeConfigs.length === 0) {
          return null;
        }

        const firstConfig = activeConfigs[0];
        const { auditCriteriaMap } = firstConfig;

        const sites = activeConfigs
          .filter((config) => config.auditSiteMap)
          .reduce((acc, config) => {
            const siteTemplate = this.siteArray.find(
              (site) => site.auditSiteId === config.auditSiteMap.id,
            );
            if (siteTemplate) {
              const calculateData = {
                ...this.findCalculationConfig(
                  auditCriteriaMap.id,
                  config.auditSiteMap.id,
                ),
                auditSiteId: config.auditSiteMap.id,
                auditCriteriaId: auditCriteriaMap.id,
              };

              acc.push({
                ...siteTemplate,
                sampleSize: _round(config.sampleSize),
                dataConfig: config,
                calculateData, // Assign the found data
              });
            }
            return acc;
          }, []);

        const criterionLevelConfig = activeConfigs.find(
          (config) => config.auditSiteMap === null,
        );

        const totalSampleSize =
          sites.length > 0
            ? sites.reduce(
                (total: number, site: AuditSamplingSite) =>
                  total + (site.sampleSize || 0),
                0,
              )
            : criterionLevelConfig
            ? _round(criterionLevelConfig.sampleSize)
            : 0;

        const criteriaCalculateData = {
          ...this.findCalculationConfig(auditCriteriaMap.id, null),
          auditCriteriaId: auditCriteriaMap.id,
          sites: _cloneDeep(sites), // Pass a snapshot of the sites for this context
        };

        return {
          auditId: this.auditId,
          criteriaId: auditCriteriaMap.criteria.id,
          auditCriteriaId: auditCriteriaMap.id,
          criteriaTitle: auditCriteriaMap.criteria.title,
          order: auditCriteriaMap.order,
          calculateData: criteriaCalculateData,
          sites,
          sampleSize: totalSampleSize,
          dataConfig: criterionLevelConfig,
        };
      })
      .filter((payload): payload is MultiSitePayload => payload !== null);
  }

  /**
   * Ensures the sites within each criterion are sorted consistently by using the
   * order of the `siteArray` as a reference. This is necessary because the API
   * returns the sites in a different order than the audit sites.
   *
   * @param payload The `multiSitePayload` to be sorted.
   * @returns The `multiSitePayload` with the sites sorted consistently.
   */
  private sortPayloadSites(payload: MultiSitePayload[]): MultiSitePayload[] {
    // Phase 3: Ensure the sites within each criterion are sorted consistently.
    const masterSiteOrder = this.siteArray.map((site) => site.siteId);

    return payload.map((criteria) => {
      const sortedSites = [...criteria.sites].sort(
        (a, b) =>
          masterSiteOrder.indexOf(a.siteId) - masterSiteOrder.indexOf(b.siteId),
      );

      return {
        ...criteria,
        sites: sortedSites,
      };
    });
  }

  public showNotificationAndRedirectToProject() {
    Toast.open({
      message: `Changes saved`,
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
  }

  /**
   * Updates the sample size of each site within the specified criteria
   * to be a proportional allocation of the total sample size.
   *
   * @param index The index of the criteria in `auditCriteriaList`.
   */
  public updateChildSamplingSize(index: number) {
    const criteria = this.auditCriteriaList[index];
    if (!criteria || !criteria.sites) {
      return;
    }

    const total = criteria.sampleSize || 0;
    const newValues = distributeTotal(total, criteria.sites.length);

    criteria.sites.forEach((site, siteIndex) => {
      site.sampleSize = newValues[siteIndex];
    });
  }

  /**
   * Updates the sample size of each site within the specified criteria, except for
   * the one at the specified site index, to be a proportional allocation of the
   * total sample size after subtracting the current sample size of the site at the
   * specified site index.
   *
   * @param index The index of the criteria in `auditCriteriaList`.
   * @param siteIndex The index of the site whose current sample size should be
   *                  subtracted from the total before redistributing the
   *                  remaining amount to the other sites.
   */
  public updateOtherChildSamplingSize(index: number, siteIndex: number) {
    const criteria = this.auditCriteriaList[index];
    if (!criteria || !criteria.sites) {
      return;
    }

    const total = criteria.sampleSize || 0;
    const currentValues = criteria.sites.map((site) => site.sampleSize || 0);

    const newValues = redistributeTotal(total, currentValues, siteIndex);

    criteria.sites.forEach((site, currentSiteIndex) => {
      site.sampleSize = newValues[currentSiteIndex];
    });
  }

  /**
   * Calculates the total sample size of the sites within the specified criteria
   * and sets the `sampleSize` property of the criteria to that value.
   *
   * @param index The index of the criteria in `auditCriteriaList`.
   */
  public updateTotalSamplingSize(index: number) {
    let total = 0;
    this.auditCriteriaList[index].sites.forEach(
      (currentVal: AuditSamplingSite, currentSiteIndex: number) => {
        total =
          Number(total) +
          Number(
            this.auditCriteriaList[index].sites[currentSiteIndex].sampleSize,
          );
      },
    );
    this.auditCriteriaList[index].sampleSize = total;
  }

  public handleMultiSiteChanges() {
    this.updateMultiSiteSampling({
      auditId: this.auditId,
      multiSitePayload: this.multiSitePayload,
      auditSamplingType: this.auditSamplingType,
      auditSamplingMode: this.auditSamplingMode,
      siteArray: this.siteArray,
      calculateData: this.criteriaBasedCalculateData,
      samplingConfigId: this.auditConfig.auditSamplingConfigs.id,
    });
  }

  /**
   * Handles the event when a sample size is applied for either a single criterion
   * and site or for all sites within a criterion.
   *
   * @param newValue The new sample size and audit site ID or audit criteria ID.
   */
  public handleApplySampleSize(newValue: any) {
    if (
      newValue.auditCriteriaId !== undefined &&
      newValue.auditSiteId !== undefined
    ) {
      const criteriIndex = _findIndex(this.auditCriteriaList, {
        auditCriteriaId: newValue.auditCriteriaId,
      });
      const siteIndex = _findIndex(this.auditCriteriaList[criteriIndex].sites, {
        auditSiteId: newValue.auditSiteId,
      });
      this.auditCriteriaList[criteriIndex].sites[
        siteIndex
      ].calculateData = newValue;
      this.auditCriteriaList[criteriIndex].sites[siteIndex].sampleSize =
        newValue.output;
      this.updateTotalSamplingSize(criteriIndex);
    } else {
      this.calculateData = newValue;
      const updateDataSiteIndex = _findIndex(this.siteArray, {
        auditSiteId: newValue.auditSiteId,
      });
      this.siteArray[updateDataSiteIndex].calculateData = newValue;
      this.auditCriteriaList.forEach(
        (currentObject: MultiSitePayload, currentIndex: number) => {
          currentObject.sites.forEach((siteValue, siteIndex) => {
            if (siteIndex === updateDataSiteIndex) {
              siteValue.sampleSize = newValue.output;
              this.updateTotalSamplingSize(currentIndex);
            }
          });
        },
      );
    }
  }

  /**
   * Handles the event when a sample size is applied for all criteria within a site.
   *
   * @param newValue The new sample size and audit site ID or audit criteria ID.
   */
  public handleApplySampleSizeForCriteriaBased(newValue: any) {
    this.criteriaBasedCalculateData = newValue;
    this.auditCriteriaList.forEach(
      (currentObject: MultiSitePayload, currentIndex: number) => {
        currentObject.sampleSize = newValue.output;
        currentObject.sites.forEach(
          (currentSiteData: AuditSamplingSite, currentSiteIndex: number) => {
            newValue.sites.forEach((siteData: any, siteIndex: number) => {
              if (currentSiteData.auditSiteId === siteData.auditSiteId) {
                currentSiteData.sampleSize = siteData.calculateData.output;
              }
            });
          },
        );
      },
    );
  }

  /**
   * Handles the event when a sample size is applied for a specific criteria within all sites.
   *
   * @param newValue The new sample size and audit site ID or audit criteria ID.
   */
  public handleApplySampleSizeForSingleCriteriaBased(newValue: any) {
    const updateDataPayloadIndex = _findIndex(this.auditCriteriaList, {
      auditCriteriaId: newValue.auditCriteriaId,
    });
    this.auditCriteriaList[updateDataPayloadIndex].sampleSize = newValue.output;
    this.auditCriteriaList[updateDataPayloadIndex].sites.forEach(
      (currentSiteData: AuditSamplingSite, currentSiteIndex) => {
        newValue.sites.forEach((siteData: any, siteIndex: number) => {
          if (currentSiteData.auditSiteId === siteData.auditSiteId) {
            currentSiteData.sampleSize = siteData.calculateData.output;
          }
        });
      },
    );
    this.auditCriteriaList[updateDataPayloadIndex].calculateData = newValue;
  }

  public handleNumericKeydown(event: KeyboardEvent): void {
    // Allow backspace, delete, tab, escape, enter, and period
    if (
      ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', '.'].includes(
        event.key,
      ) ||
      // Allow: Ctrl+A, Command+A
      (event.key === 'a' &&
        (event.ctrlKey === true || event.metaKey === true)) ||
      // Allow: home, end, left, right, down, up
      [
        'Home',
        'End',
        'ArrowLeft',
        'ArrowRight',
        'ArrowDown',
        'ArrowUp',
      ].includes(event.key)
    ) {
      // let it happen, don't do anything
      return;
    }
    // Ensure that it is a number and stop the keypress if not
    if (
      event.shiftKey ||
      !['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.'].includes(
        event.key,
      )
    ) {
      event.preventDefault();
    }
  }

  public enforceMaxLength(event: Event): void {
    const target = event.target as HTMLInputElement;
    const maxLength = target.getAttribute('maxlength');
    if (maxLength && target.value.length > parseInt(maxLength, 10)) {
      target.value = target.value.slice(0, parseInt(maxLength, 10));
    }
  }

  get auditCriteriaList() {
    return _orderBy(this.multiSitePayload, ['order'], ['asc']);
  }

  /**
   * Find the calculation configuration for the given audit criteria ID and
   * audit site ID. The configuration is determined in the following order of
   * precedence:
   *
   * 1. A configuration with both a matching criteria ID and site ID.
   * 2. A configuration with a matching criteria ID, but no site ID.
   * 3. The global configuration, with neither a criteria ID nor site ID.
   *
   * @param {number|null} auditCriteriaId - The ID of the audit criteria to find
   *  the configuration for.
   * @param {number|null} auditSiteId - The ID of the audit site to find the
   *  configuration for.  If null, the global configuration is used.
   *
   * @return {Object} - The calculation configuration to use as the base.
   */
  private findCalculationConfig(
    auditCriteriaId: number | null,
    auditSiteId: number | null,
  ) {
    const configs = this.auditConfig.calculationConfigs || [];

    // Find the global configuration (where both IDs are null)
    const globalConfig = configs.find(
      (c: AuditSamplingDataConfig) => !c.auditCriteriaMap && !c.auditSiteMap,
    );

    // Find the most specific configuration matching the provided IDs
    const specificConfig = configs.find((c: AuditSamplingDataConfig) => {
      const criteriaMapId = c.auditCriteriaMap ? c.auditCriteriaMap.id : null;
      const siteMapId = c.auditSiteMap ? c.auditSiteMap.id : null;

      const criteriaMatch = auditCriteriaId === criteriaMapId;
      const siteMatch = auditSiteId === siteMapId;
      return criteriaMatch && siteMatch;
    });

    // Determine which configuration to use as the base
    const baseCalculation =
      (specificConfig && specificConfig.calculation) ||
      (globalConfig && globalConfig.calculation) ||
      {};

    // Merge the found configuration with the hardcoded default to ensure all fields are present
    return {
      ..._cloneDeep(this.oneOffCalculateData),
      ...baseCalculation,
    };
  }
}
</script>
<style lang="scss" module>
.tableName {
  color: #444f51;
  font-weight: bold;
}
.saveChangesBtn {
  float: right;
  border-radius: 24px;
  height: 48px;
  width: 120px;
  padding: 0 calc(1em + 0.25em);
}
.paddingTop {
  padding-top: 30px;
}
.criteriaBasedLabel {
  margin-top: 12px;
  padding-top: 24px;
}
.criteriaBasedLabelSiteBased {
  margin-top: 12px;
  padding-top: 32px;
}
.changePaddingTop {
  margin-top: 12px;
  padding-top: 24px !important;
}
.saveChangesContainer {
  padding-top: 60px;
  text-align: right;
}
.numberInput {
  width: 80px !important;
  height: 44px;
  input {
    background-color: white !important;
    border-color: #dfdfdf !important;
    color: #444f51 !important;
    text-align: right;
    height: 44px;
    padding: 7px 7px;
  }
}
input[type='number'] {
  -moz-appearance: textfield;
}
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
.calculatorHeader {
  float: right;
}
.headerTitleColumn {
  padding: 0 0.75rem;
}
.criteriaBasedTable > div > table {
  thead {
    background-color: #ffffff;
    tr {
      font-size: 14px;
      th {
        color: #9f9f9f;
        font-weight: initial !important;
        padding: 20px 0px 23px 12px;
      }
      th:first-child {
        padding-left: 20px;
      }
      th:last-child {
        padding-left: 0;
        padding-right: 20px;
      }
      th:nth-child(2) {
        padding-left: 42px;
      }
      th:nth-child(3) {
        div {
          float: right;
        }
      }
    }
  }
  tbody {
    background-color: #ffffff;
    tr {
      td {
        color: #444f51;
        border: 0.5px solid #dbdbdb;
        border-width: 0 0 0.5px;
        padding-top: 20px;
        padding-bottom: 18px;
        padding-right: 0px;
      }
      td:first-child {
        padding-left: 20px;
      }
      td:last-child {
        padding-left: 0;
        padding-right: 20px;
        padding-top: 30px;
      }
      td:nth-child(2) {
        padding-left: 42px;
      }
      td:nth-child(3) {
        float: right;
        width: 100%;
      }
    }
    tr:last-child {
      td {
        padding-bottom: 20px;
      }
    }
    tr:hover {
      background-color: #f7fbff;
      td:last-child > span > div > img {
        visibility: visible;
      }
    }
  }
}
.siteBasedTable > div > table {
  thead {
    background-color: #ffffff;
    tr {
      font-size: 14px;
      th {
        color: #9f9f9f;
        font-weight: initial !important;
        padding: 20px 0px 23px 12px;
      }
      th:first-child {
        padding-left: 20px;
      }
      th:last-child {
        padding-left: 0;
        padding-right: 20px;
      }
      th:nth-child(2) {
        padding-left: 42px;
      }
      th:nth-child(3) {
        div {
          float: right;
        }
      }
    }
  }
  tbody {
    background-color: #ffffff;
    tr {
      td {
        color: #444f51;
        border: 0.5px solid #dbdbdb;
        border-width: 0 0 0.5px;
        padding-top: 20px;
        padding-bottom: 18px;
        padding-right: 0px;
      }
      td:first-child {
        padding-left: 20px;
      }
      td:last-child {
        padding-left: 0;
        padding-right: 20px;
        padding-top: 30px;
      }
      td:nth-child(2) {
        padding-left: 42px;
      }
      td:nth-child(3) {
        float: right;
        width: 100%;
      }
    }
    tr:last-child {
      td {
        padding-bottom: 20px;
      }
    }
    tr:hover {
      background-color: #f7fbff;
      td:last-child > span > div > img {
        visibility: visible;
      }
    }
  }
}
</style>
